import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ColorUtil, isDummyUserName } from '@luggagehero/shared/util';
import { Subject } from 'rxjs';

@Component({
  selector: 'lh-shared-ui-user-profile-image',
  imports: [CommonModule],
  templateUrl: './shared-ui-user-profile-image.component.html',
  styleUrl: './shared-ui-user-profile-image.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedUiUserProfileImageComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public imageUrl?: string;
  @Input() public name?: string;
  @Input() public color!: string;
  @Input() public size: 'sm' | 'md' | 'lg' = 'md';
  @Input() public profileBackgroundColors!: string[];
  /**
   * When true and `imageUrl` is set, the image is rendered as-is without our
   * circular clip or outline. Use this for third-party photos that ship with
   * their own styling/decorations — e.g. Google Business reviews, which
   * occasionally include a small badge in the bottom-right corner that we
   * mustn't crop.
   *
   * Internal user photos (our own sign-up avatars) should leave this as `false`
   * so they get the standard circular treatment.
   */
  @Input() public preserveImage = false;

  public isDummy = false;
  public imageLoadError = false;

  private destroy$: Subject<unknown> = new Subject();
  private _cd = inject(ChangeDetectorRef);

  public ngOnInit() {
    this.updateState();
  }

  public ngOnChanges() {
    this.imageLoadError = false;
    this.updateState();
  }

  private updateState(): void {
    this.isDummy = isDummyUserName(this.name ?? '');
    if (!this.color) {
      this.color =
        this.name && !this.isDummy
          ? ColorUtil.pickColorByUsername(this.name, this.profileBackgroundColors)
          : ColorUtil.pickRandomColor(this.profileBackgroundColors);
    }

    this._cd.markForCheck();
  }

  public onImageError(): void {
    this.imageLoadError = true;
    this._markForCheck();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  protected _detectChanges(): void {
    this._cd.detectChanges();
  }

  protected _markForCheck(): void {
    this._cd.markForCheck();
  }
}
